/**
 * Asset Type Filter Dialog (New Version)
 * 
 * Uses real-time database queries for accurate asset counts
 * Replaces the old cached version to fix count accuracy issues
 */

import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AnyPosition } from "@/components/investments/dialog/types";
import { mapPositionToAssetType } from "@/components/investments/dialog/mapPositionToAssetType";
import { logger } from "@/services/LoggingService";


// Using shared mapPositionToAssetType to ensure consistent classification across app
const IS_DEV = import.meta.env?.DEV ?? false;

interface AssetTypeFilterDialogProps {
  isOpen?: boolean;
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  positions: AnyPosition[];
  onContinue: (filteredPositions: AnyPosition[]) => void;
}

export function AssetTypeFilterDialog({
  isOpen,
  open,
  onClose,
  onOpenChange,
  positions,
  onContinue,
}: AssetTypeFilterDialogProps) {
  // Handle both open and isOpen props for compatibility
  const isDialogOpen = open ?? isOpen ?? false;
  
  // Handle both onClose and onOpenChange callbacks
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (onClose) onClose();
      if (onOpenChange) onOpenChange(false);
    }
  };

  // Track which asset types to include (match database values)
  const [selectedTypes, setSelectedTypes] = useState({
    cash: true,
    equity: true,
    fixed_income: true,
    alternative: true,
    multi_asset: true,
    unclassified: true
  });
  
  // FIXED: Use the positions passed from parent (now ALL database holdings)
  // This ensures we count ALL assets from the database, not just visible ones
  const { typeCounts, holdingsCount, assetsCount } = useMemo(() => {
    if (!positions || (positions?.length || 0) === 0) {
      if (isDialogOpen) {
        logger.warn('AssetTypeFilterDialogNew', 'ASSET COUNT ISSUE - No positions provided to dialog', {
          positionsLength: positions?.length || 0,
          positionsType: typeof positions,
          timestamp: new Date().toISOString()
        });
      }
      return { typeCounts: {}, holdingsCount: 0, assetsCount: 0 };
    }
    
    if (IS_DEV) {
      logger.info('AssetTypeFilterDialogNew', 'ASSET COUNT PROCESSING - Calculating type counts', {
        totalPositions: positions?.length || 0,
        sample: (positions || []).slice(0, 3).map(p => ({ name: p.name, securityType: p.securityType }))
      });
    }
    
    const counts: Record<string, number> = {};
    const positionDetails: Record<string, any[]> = {};
    let holdingsTotal = 0;
    let assetsTotal = 0;
    
    // Ensure positions is always an array to prevent null reference errors
    const safePositions = positions || [];
    safePositions.forEach((position, index) => {
      const assetType = mapPositionToAssetType(position);
      counts[assetType] = (counts[assetType] || 0) + 1;
      
      // Determine if this is a manual asset or connected holding
      const isManualAsset = ('provider' in position && position.provider === 'manual') || 
                           ('accountId' in position && position.accountId === 'manual');
      
      if (isManualAsset) {
        assetsTotal++;
      } else {
        holdingsTotal++;
      }
      
      // Track position details for debugging
      if (!positionDetails[assetType]) {
        positionDetails[assetType] = [];
      }
      positionDetails[assetType].push({
        index,
        name: position.name,
        securityType: position.securityType,
        accountId: position.accountId || 'unknown',
        isManual: isManualAsset
      });
    });
    
    if (IS_DEV) {
      logger.debug?.('AssetTypeFilterDialogNew', 'DETAILED asset type analysis', {
        totalPositions: positions?.length || 0,
        holdingsCount: holdingsTotal,
        assetsCount: assetsTotal,
        counts,
        // Keep payload small in dev; omit full positionDetails to avoid serialization cost
        sample: (positions || []).slice(0, 2).map(p => ({
          name: p.name,
          mappedType: mapPositionToAssetType(p),
        })),
      } as any);
    }
    
    return { typeCounts: counts, holdingsCount: holdingsTotal, assetsCount: assetsTotal };
  }, [positions, isDialogOpen]);
  
  // Continue with filtered positions
  const handleContinue = () => {
    const filtered = (positions || []).filter(position => {
      const assetType = mapPositionToAssetType(position);
      return selectedTypes[assetType];
    });
    
    if (IS_DEV) {
      logger.info('AssetTypeFilterDialogNew', 'Filtered positions for portfolio', {
        originalCount: positions?.length || 0,
        filteredCount: filtered?.length || 0,
      });
    }
    
    onContinue(filtered);
  };
  
  // Select/deselect all types
  const handleSelectAll = (selected: boolean) => {
    setSelectedTypes({
      cash: selected,
      equity: selected,
      fixed_income: selected,
      alternative: selected,
      multi_asset: selected,
      unclassified: selected
    });
  };
  
  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Asset Types to Include</DialogTitle>
          <DialogDescription>
            Choose which type of holdings to add to a portfolio
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Select/Deselect All */}
          <div className="flex items-center justify-between border-b pb-2">
            <span className="font-medium">Asset Type</span>
            <div className="space-x-2">
              <Button variant="outline" size="sm" onClick={() => handleSelectAll(true)}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleSelectAll(false)}>
                Deselect All
              </Button>
            </div>
          </div>
          
          {/* Individual type checkboxes with real-time counts */}
          {Object.entries(selectedTypes).map(([type, checked]) => (
            <div className="flex items-center justify-between" key={type}>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`type-${type}`}
                  checked={checked}
                  onCheckedChange={(checked) => 
                    setSelectedTypes(prev => ({ ...prev, [type]: !!checked }))
                  }
                />
                <label 
                  htmlFor={`type-${type}`} 
                  className="cursor-pointer"
                >
                  {type === 'unclassified' ? 'Unclassified' : 
                   type === 'multi_asset' ? 'Multi-Asset' :
                   type === 'fixed_income' ? 'Fixed Income' :
                   (type || '').charAt(0).toUpperCase() + (type || '').slice(1).replace('_', ' ')}
                </label>
              </div>
              <span className="text-xs text-muted-foreground">
                {typeCounts[type] || 0} holdings
              </span>
            </div>
          ))}
          
          <div className="text-xs text-muted-foreground pt-2 border-t">
            Total: {holdingsCount > 0 && assetsCount > 0 ? `${holdingsCount} holdings, ${assetsCount} assets` : 
                   holdingsCount > 0 ? `${holdingsCount} holdings` : 
                   assetsCount > 0 ? `${assetsCount} assets` : 
                   '0 items'}
          </div>

          {/* Friendly explanatory note below totals to match dialog patterns */}
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground mt-2">
            <span className="font-medium">Note:</span> Some items you see on the Assets page are account ‘containers’. We exclude those here so your portfolio isn’t counted twice.
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button onClick={handleContinue}>Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
